/**
 * Shared helpers for picking THE aircraft option to show once the client
 * has (or hasn't yet) picked and paid for one. Every FE surface that
 * displays "the booked aircraft" post-payment should go through one of
 * these two functions so the rule stays consistent across QuoteCard, the
 * Quote Details drawer, the trip-sheet pages/PDFs, and every email that
 * renders the tail/aircraft the client chose.
 *
 * The rule
 * --------
 * `bid_option.status` lifecycle:
 *   - 'DRAFT'    — freshly shortlisted on /tails, not yet attached to any
 *                  quote. The Shortlist drawer's pre-quote view.
 *   - 'SENT'     — attached to a quote (Quick Quote / Trip Group / Edit /
 *                  Duplicate). Every create/attach path writes this.
 *   - 'ACCEPTED' — the client picked this option and paid. Written exactly
 *                  once per bid inside the checkout success path (see
 *                  CardInfoForm.payQuote). Siblings stay SENT.
 *   - 'REJECTED' — the broker (or a PATCH soft-delete) removed the option
 *                  from consideration. Kept in lockstep with
 *                  `shortlisted:false` so legacy readers still work.
 *
 * Legacy note: older bid_option rows in the DB may only have DRAFT and
 * ACCEPTED values (from before this scheme). SENT/REJECTED are additive;
 * every reader that keys on `shortlisted !== false` still works because
 * we write both fields in lockstep.
 *
 * Which helper to use
 * -------------------
 * `selectAcceptedBidOption` is STRICT — returns null when no option is
 *   ACCEPTED yet. Use this on surfaces that RENDER SOMETHING ELSE (like
 *   the whole shortlist grid) when pre-payment, and only need to detect
 *   "should we collapse to the winner" (QuoteCard summary, drawer's
 *   aircraft-cards grid, "Book This Flight" button gating).
 *
 * `selectPrimaryBidOption` FALLS BACK — returns the shortlisted-and-not-
 *   accepted option (or the first raw option) when no ACCEPTED exists.
 *   Use this on surfaces that ALWAYS render exactly one option (trip-sheet
 *   page, trip-sheet PDF, "booking secured" email, flight tracking email)
 *   so those surfaces have SOMETHING to render even in pre-payment /
 *   data-drift edge cases.
 */

/**
 * Return the client's picked-and-paid option, or null if none yet.
 *
 * @param {Array} bidOptions - Options from bid.bid_options
 * @returns {Object|null}
 */
export const selectAcceptedBidOption = (bidOptions) =>
  (bidOptions || []).find((o) => o?.status === 'ACCEPTED') || null;

/**
 * Return a single "primary" option — the ACCEPTED one if present,
 * otherwise the first shortlisted, otherwise the first raw option.
 * Never null unless the bid_options array is itself empty or falsy.
 *
 * The three-tier fallback exists because trip-sheet-style surfaces need
 * to render something meaningful pre-payment too (e.g. previewing the
 * trip sheet against the currently-recommended aircraft).
 *
 * @param {Array} bidOptions - Options from bid.bid_options
 * @returns {Object|null}
 */
export const selectPrimaryBidOption = (bidOptions) => {
  // Exclude soft-deleted / rejected options from every branch. Under the
  // status-in-lockstep-with-shortlisted scheme, a REJECTED option always
  // carries `shortlisted:false` (and vice versa on legacy rows), so a
  // single filter covers both signals. Prevents the last-resort `opts[0]`
  // branch from surfacing a "removed" option on data-drift edge cases —
  // e.g. a post-payment quote whose ACCEPTED row is missing.
  const opts = (bidOptions || []).filter((o) => o?.shortlisted !== false);
  return (
    opts.find((o) => o?.status === 'ACCEPTED')
    || opts.find((o) => o?.shortlisted === true)
    || opts[0]
    || null
  );
};
